import { Column, Scrolling, SearchPanel } from 'devextreme-react/data-grid';
import React, { useMemo } from 'react';
import DatagridCustom from '../../../components/common/datagrid/custom-datagrid';
import useFormatMessage from '../../../utils/useFormatMessage';

const HIDDEN_FIELDS = new Set([
    'sase', 'process_type', 'original invoice number', 'client code', 'client invoice date',
    'client name', 'commercial denomination name', 'gemi adı', 'gemi varış tarihi'
]);
const DEFINITION_FIELDS = new Set([
    'marka', 'commercial denomination code', 'car line', 'colour code', 'colour name', 'type'
]);
const LABELS = {
    'marka': 'Marka', 'commercial denomination code': 'Model kodu', 'car line': 'Model',
    'colour code': 'Renk kodu', 'colour name': 'Renk', 'type': 'Araç tipi',
    'pdi': 'PDI durumu', 'planlama tarihi': 'Planlama tarihi', 'yeni/iptal': 'Emir durumu',
    'sefer no': 'Sefer no', 'ship_unload.unload_date': 'Tahliye tarihi', 'unload_date': 'Tahliye tarihi',
    'customs_clearance.beyanname no': 'Beyanname no', 'customs_clearance.tarih': 'Beyanname tarihi',
    'customs_clearance.clearance_date': 'Gümrüklenme tarihi', 'pdi_entry': 'PDI giriş',
    'pdi_entry_date': 'PDI giriş tarihi', 'pdi_exit': 'PDI çıkış', 'pdi_exit_date': 'PDI çıkış tarihi',
    'from_warehouse_id': 'Çıkış deposu', 'to_warehouse_id': 'Varış deposu',
    'warning.info': 'Uyarı', 'warning.date': 'Uyarı tarihi'
};
const EVENT_RESULTS = {
    'PDI İPTAL': 'PDI çıkış kaydı iptal edildi',
    'PDI EMRİ İPTAL': 'PDI emri iptal edildi'
};

const flatten = (value, prefix = '', result = {}) => {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return result;
    Object.entries(value).forEach(([key, child]) => {
        const path = prefix ? `${prefix}.${key}` : key;
        if (!key || HIDDEN_FIELDS.has(path) || HIDDEN_FIELDS.has(key)) return;
        if (child && typeof child === 'object' && !Array.isArray(child)) {
            flatten(child, path, result);
        } else if (child !== null && child !== undefined && child !== '') {
            result[path] = child;
        }
    });
    return result;
};

const normalizedValue = (value) => {
    if (value === true || value === 1 || value === '1') return 'Evet';
    if (value === false || value === 0 || value === '0') return 'Hayır';
    return String(value ?? '');
};

const Tarihce = ({ data }) => {
    const { t } = useFormatMessage();
    const processedData = useMemo(() => {
        if (!Array.isArray(data)) return [];
        let previousSnapshot = {};
        return data.filter(Boolean).map((item, index) => {
            const snapshot = flatten(item.data || {});
            let changedEntries;
            if (index === 0 || item?.operation?.includes?.('ŞASE TANIMLAMA')) {
                changedEntries = Object.entries(snapshot).filter(([key]) => DEFINITION_FIELDS.has(key));
            } else {
                changedEntries = Object.entries(snapshot).filter(([key, value]) => previousSnapshot[key] !== value);
            }
            // Each log usually contains the full vehicle snapshot. Comparing snapshots keeps
            // inherited fields out of later events without changing the immutable log itself.
            previousSnapshot = snapshot;
            if (item.operation === 'GÜMRÜK İŞLEMİ') {
                changedEntries = Object.entries(snapshot).filter(([key]) => key.startsWith('customs_clearance.'));
            }
            const details = changedEntries.map(([key, value]) => ({
                key, label: LABELS[key] || t(key), value: normalizedValue(value)
            }));
            if (details.length === 0) {
                details.push({ key: 'event', label: 'Sonuç', value: EVENT_RESULTS[item.operation] || 'İşlem kaydedildi' });
            }
            return {
                ...item,
                event_details: details,
                event_details_search: details.map(detail => `${detail.label}: ${detail.value}`).join(' · ')
            };
        });
    }, [data, t]);

    const detailsCell = ({ data: row }) => (
        <div style={{ whiteSpace: 'normal', minWidth: '320px', lineHeight: '1.45' }}>
            {row.event_details.map(detail => (
                <div key={`${detail.key}-${detail.value}`}>
                    <strong>{detail.label}:</strong> {detail.value}
                </div>
            ))}
        </div>
    );

    return (
        <div className="h-full w-full overflow-hidden">
            <DatagridCustom
                width="100%" height="100%" columnAutoWidth={false} allowColumnResizing={true}
                showBorders={true} wordWrapEnabled={true} dataSource={processedData}
                style={{ maxHeight: '450px' }}
            >
                <SearchPanel visible={true} width={240} placeholder="Ara..." />
                <Column caption={t('Dosya Adı')} dataField="file_name" width={245} />
                <Column caption={t('Yükleme Tarihi')} dataField="process_date" dataType="datetime"
                    format="dd/MM/yyyy HH:mm" width={145} />
                <Column caption={t('Kullanıcı')} dataField="username" width={165} />
                <Column caption={t('Olay Adı')} dataField="operation" width={180} />
                <Column caption="Olay Detayları" dataField="event_details_search" cellRender={detailsCell} minWidth={360} />
                <Scrolling mode="virtual" showScrollbar="always" useNative={false} />
            </DatagridCustom>
        </div>
    );
};

export default Tarihce;
